"use client";

import React from "react";
import { ArrowRight, Wrench, ShieldAlert, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 4,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedProductsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({
        length: 4
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col justify-between rounded-sm border border-border bg-card p-5 animate-pulse min-h-[420px]">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="h-4 w-16 bg-muted rounded-sm" />
                <div className="h-4 w-12 bg-muted rounded-sm" />
              </div>
              <div className="aspect-square w-full rounded-sm bg-muted" />
              <div className="h-5 w-3/4 bg-muted rounded-sm" />
              <div className="h-4 w-1/2 bg-muted rounded-sm" />
            </div>
            <div className="mt-6 space-y-3">
              <div className="h-7 w-1/3 bg-muted rounded-sm" />
              <div className="h-9 w-full bg-muted rounded-sm" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-sm border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to load workshop equipment.
        </p>
        <Button onClick={() => refetch()} variant="outline" size="sm" className="mt-4 font-mono text-xs uppercase">
          Retry connection
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="rounded-sm border border-border bg-card p-12 text-center">
        <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">
          No featured equipment currently assigned
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => {
      const categoryName = product.categories?.[0]?.name || "Shop Gear";
      const isSale = product.on_sale;
      return <article key={product.id} data-index={i} className="group relative flex flex-col justify-between rounded-sm border border-border bg-card p-5 transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-lg">
            <div>
              {/* Card Meta Header */}
              <div className="mb-3 flex items-center justify-between text-xs font-mono text-muted-foreground">
                <span className="flex items-center gap-1.5 uppercase tracking-wider">
                  <Wrench className="size-3 text-primary" />
                  {categoryName}
                </span>
                {isSale ? <Badge variant="default" className="rounded-xs bg-primary text-primary-foreground font-mono text-[10px] uppercase font-bold tracking-wider px-1.5 py-0">
                    Sale
                  </Badge> : <span className="text-[11px] text-muted-foreground/80 uppercase">
                    SKU #{product.id}
                  </span>}
              </div>

              {/* Product Thumbnail Container */}
              <Link to={product.link} className="relative block aspect-square w-full overflow-hidden rounded-xs bg-muted/40 border border-border/50">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-contain p-4 transition-transform duration-300 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/60 text-muted-foreground">
                    <Cpu className="size-10 opacity-40" />
                  </div>}
              </Link>

              {/* Product Title */}
              <h3 className="mt-4 font-serif text-lg font-bold uppercase tracking-tight text-card-foreground line-clamp-2 leading-tight">
                <Link to={product.link} className="hover:text-primary transition-colors duration-150">
                  {product.title}
                </Link>
              </h3>

              {/* Monospace Technical Summary if available */}
              {product.short_description ? <div className="mt-2 text-xs font-mono text-muted-foreground line-clamp-2 [&_p]:m-0" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} /> : <p className="mt-2 text-xs font-mono text-muted-foreground">
                  Heavy-gauge steel fabrication rating.
                </p>}
            </div>

            {/* Price Lockup and Add To Cart */}
            <div className="mt-6 pt-4 border-t border-border/70 space-y-3">
              <div className="flex items-center justify-between">
                <div className="border-l-2 border-primary pl-2.5 py-0.5">
                  <span className="block text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    Duty Spec Price
                  </span>
                  <ProductPrice product={product} className="font-serif text-xl font-bold tracking-tight text-foreground" />
                </div>
              </div>

              <div className="w-full">
                <AddToCartButton product={product} productId={product.id} variant="default" size="sm" text="Add to Cart" buttonClassName="w-full bg-primary text-primary-foreground font-serif uppercase font-bold tracking-wider rounded-xs hover:brightness-110 transition-[filter]" showQuantity={false} className="w-full" />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Featured() {
  return <section id="featured-products" data-nav="dark" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            {/* Technical Eyebrow Tag */}
            <div className="inline-flex items-center gap-2 font-mono text-xs font-bold tracking-wider text-primary uppercase mb-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span>Workshop catalog // tier 01-04</span>
            </div>

            {/* Section Headline with Precision Spec Rule */}
            <div className="flex items-start gap-3">
              <div className="h-8 md:h-10 w-[3px] bg-primary shrink-0 mt-1" />
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground leading-none">
                Featured workshop gear
              </h2>
            </div>

            <p className="mt-3 text-sm sm:text-base font-sans text-muted-foreground max-w-2xl pl-4">
              Commercial-grade tools and shop hardware built for heavy-duty
              workshop performance.
            </p>
          </div>

          {/* Desktop Direct Action */}
          <div className="hidden md:flex shrink-0">
            <Button asChild variant="outline" size="default" className="font-serif font-bold uppercase tracking-wider rounded-xs border-border bg-card text-foreground hover:border-foreground hover:bg-accent">
              <Link to="/ShopPage">
                <span>View Full Catalog</span>
                <ArrowRight className="size-4 ml-1.5 text-primary" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Dynamic WooCommerce Grid Container */}
        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        order: "desc",
        orderby: "date"
      }}>
          <FeaturedProductsConsumer />
        </EcommerceProductsProvider>

        {/* Mobile View All Link */}
        <div className="mt-8 flex justify-center md:hidden">
          <Button asChild variant="outline" className="w-full font-serif font-bold uppercase tracking-wider rounded-xs border-border bg-card text-foreground hover:border-foreground">
            <Link to="/ShopPage">
              <span>View Full Catalog</span>
              <ArrowRight className="size-4 ml-1.5 text-primary" />
            </Link>
          </Button>
        </div>

        {/* Technical Footer Assurance Bar */}
        <div className="mt-12 pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="text-primary">01.</span>
            <span>Commercial-rated torque and structural tolerance</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary">02.</span>
            <span>Nationwide continental US delivery</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary">03.</span>
            <span>Technical customer service and spec verification</span>
          </div>
        </div>
      </div>
    </section>;
}